/**
 * Resolução do destino do logout (Login V2).
 *
 * O Zitadel, ao processar `/oidc/v1/end_session`, NÃO redireciona sozinho para o
 * `post_logout_redirect_uri` quando a UI de logout V2 está configurada: ele manda
 * o browser para `ZITADEL_OIDC_DEFAULTLOGOUTURLV2` (aqui:
 * `https://login.groupws.com.br/logout?post_logout_redirect=<uri>`) e delega a
 * volta para NÓS. Sem uma rota `/logout` que faça esse último salto, o usuário
 * fica parado na tela do login e nunca volta para o app.
 *
 * O nome do parâmetro é o que o Zitadel anexa (`post_logout_redirect`), mas
 * aceitamos os apelidos comuns por robustez — versões do Zitadel e a UI
 * referencial usam `post_logout_redirect_uri` / `redirect`.
 */
const PARAMS_ACEITOS = ['post_logout_redirect', 'post_logout_redirect_uri', 'redirect'] as const;

/**
 * Destino final do logout, já validado.
 *
 * O valor vem da URL, então é entrada de usuário: aceitar qualquer coisa abriria
 * um **open redirect** partindo de um domínio confiável (login.groupws.com.br).
 * Por isso só devolvemos a URL pedida quando a ORIGEM dela bate com a de um dos
 * destinos permitidos; qualquer outra coisa cai no app principal.
 *
 * @param search  query string da rota (`window.location.search`, com ou sem `?`)
 * @param appUrl  URL do app principal (`VITE_APP_URL`) — destino padrão
 * @param origensExtras  outras origens permitidas (ex.: o próprio login)
 */
export function resolverDestinoPosLogout(
  search: string,
  appUrl: string,
  origensExtras: readonly string[] = [],
): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  let pedido: string | null = null;
  for (const nome of PARAMS_ACEITOS) {
    const valor = params.get(nome);
    if (valor) {
      pedido = valor;
      break;
    }
  }

  if (!pedido) return appUrl;

  const permitidas = new Set(
    [appUrl, ...origensExtras].map((url) => origemDe(url)).filter((o): o is string => o !== null),
  );

  const origemPedida = origemDe(pedido);
  if (origemPedida !== null && permitidas.has(origemPedida)) return pedido;

  return appUrl;
}

function origemDe(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}
